import { configure, defineRule } from 'vee-validate'
import { i18n } from '../localization'

// @ts-ignore
import { email, is_not, max, min, one_of, required } from '@vee-validate/rules'
import { formatDate, parseDate } from '../utilities/helpers'
import { isValid, parse, subYears } from 'date-fns'
import { array, object, string } from 'yup'

configure({
    // TODO: maybe use veeValidate i18n library: https://vee-validate.logaretm.com/v4/guide/i18n
    generateMessage: (context) => {
        if (context.rule?.name) {
            switch (context.rule.name) {
                case 'min':
                    return i18n.global.t('validation_error.min', {
                        field: i18n.global.t(context.field),
                        length: context.rule.params[0],
                    })
                case 'max':
                    return i18n.global.t('validation_error.max', {
                        field: i18n.global.t(context.field),
                        length: context.rule.params[0],
                    })
                case 'required':
                    return i18n.global.t('validation_error.required')
                case 'email':
                    return i18n.global.t('validation_error.email')
                default:
                    return i18n.global.t('validation_error.invalid')
            }
        }
        // return i18n.global.t('validation_error.invalid')
        // return `Das Feld ${context.field} ist ungültig.`
    },
})

defineRule('min', min)
defineRule('max', max)
defineRule('required', required)
defineRule('email', email)
defineRule('is_not', is_not)
defineRule('one_of', one_of)

/*
 * define additional global validation rules: https://vee-validate.logaretm.com/v4/guide/global-validators
 */

defineRule('requiredArray', (value) => {
    if (value.length === 0) {
        return i18n.global.t('validation_error.required')
    }
    return true
})

defineRule('date', (value) => {
    const date = parse(value, 'yyyy-MM-dd', new Date())
    if (value != null && !isValid(date)) {
        return i18n.global.t('validation_error.wrong_date_format')
    }
    return true
})

defineRule('not_equals', (value, [target, msg]) => {
    if (value != target) {
        return true
    }
    return msg || i18n.global.t('validation_error.not_equals')
})

defineRule('date_min', (value, [min]) => {
    if (parseDate(value) >= parseDate(min)) {
        return true
    }
    return i18n.global.t('validation_error.date_min', { date: formatDate(min) })
})

defineRule('required_if', (value, [target], ctx) => {
    const currentValue = getValueFromObject(target, ctx.form)
    if (currentValue) {
        if (value === null || value === undefined || value === '') {
            return i18n.global.t('validation_error.required')
        }
    }
    return true
})

defineRule('required_unless', (value, [target, targetValue]: [string, any], ctx) => {
    const currentValue = getValueFromObject(target, ctx.form)

    if (targetValue !== undefined) {
        if (targetValue !== currentValue) {
            if (value === null || value === undefined || value === '') {
                return i18n.global.t('validation_error.required')
            }
        }
    } else if (!currentValue) {
        if (value === null || value === undefined || value === '') {
            return i18n.global.t('validation_error.required')
        }
    }
    return true
})

defineRule('date_max', (value, [min]) => {
    if (parseDate(value) <= parseDate(min)) {
        return true
    }
    return i18n.global.t('validation_error.date_max', { date: formatDate(min) })
})

defineRule('min_age', (value, [years]) => {
    if (parseDate(value) <= subYears(new Date(), years)) {
        return true
    }
    return i18n.global.t('validation_error.min_age', { years })
})

defineRule('max_age', (value, [years]) => {
    if (parseDate(value) >= subYears(new Date(), years)) {
        return true
    }
    return i18n.global.t('validation_error.max_age', { years })
})

defineRule('countryISO3166', (value) => {
    // Check if email
    if (!/^\w\w\w$/.test(value)) {
        return i18n.global.t('validation_error.countryISO3166')
    }
    return true
})

defineRule('iban', (value) => {
    if (!!value && !(smellsLikeIban(value) && validateIbanChecksum(value))) {
        return i18n.global.t('validation_error.iban')
    }
    return true
})

defineRule('bic', (value) => {
    if (
        !!value &&
        !/^([a-zA-Z]{4})([a-zA-Z]{2})(([2-9a-zA-Z]{1})([0-9a-np-zA-NP-Z]{1}))((([0-9a-wy-zA-WY-Z]{1})([0-9a-zA-Z]{2}))|([xX]{3})|)$/.test(
            value
        )
    ) {
        return i18n.global.t('validation_error.bic')
    }
    return true
})

function smellsLikeIban(str) {
    return /^([A-Z]{2}[ -]?[0-9]{2})(?=(?:[ -]?[A-Z0-9]){9,30}$)((?:[ -]?[A-Z0-9]{3,5}){2,7})([ -]?[A-Z0-9]{1,3})?$/.test(
        str.trim()
    )
}

/**
 * source:https://stackoverflow.com/a/44657292/4482393https://stackoverflow.com/a/44657292/4482393
 * @param iban
 */
function validateIbanChecksum(iban) {
    const ibanStripped = iban
        .replace(/[^A-Z0-9]+/gi, '') //keep numbers and letters only
        .toUpperCase() //calculation expects upper-case
    const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/)
    if (!m) return false

    const numbericed = (m[3] + m[1] + m[2]).replace(/[A-Z]/g, function (ch) {
        //replace upper-case characters by numbers 10 to 35
        return ch.charCodeAt(0) - 55
    })
    //The resulting number would be to long for javascript to handle without loosing precision.
    //So the trick is to chop the string up in smaller parts.
    const mod97 = numbericed.match(/\d{1,7}/g).reduce(function (total, curr) {
        return Number(total + curr) % 97
    }, '')

    return mod97 === 1
}

export function getTinValidationRules() {
    return array()
        .of(
            object().shape(
                {
                    country: string().required().label('Country'),
                    tin: string()
                        .when(['no_tin_reason'], {
                            is: (val) => !val || val === '',
                            then: (schema) => schema.required(),
                            otherwise: (schema) => schema.optional().nullable(),
                        })
                        .label('TIN'),
                    no_tin_reason: string()
                        .when(['tin'], {
                            is: (val) => !val || val === '',
                            then: (schema) => schema.required(),
                            otherwise: (schema) => schema.optional().nullable(),
                        })
                        .label('No TIN Reason'),
                },
                [['tin', 'no_tin_reason']]
            )
        )
        .strict()
}

const getValueFromObject = (path: string, object: object) => {
    return path.split('.').reduce((o, i) => o[i], object)
}
